export type VideoPlatform = 'instagram' | 'youtube' | 'tiktok';

// Match by hostname only — share-link path formats change too often to
// pattern-match (/reel/ vs /reels/ vs /share/, vm. vs vt. short links).
// Whether a URL actually yields a video is decided by extraction/embedding.
const PLATFORM_HOSTS: { platform: VideoPlatform; domains: string[] }[] = [
  { platform: 'instagram', domains: ['instagram.com'] },
  { platform: 'youtube', domains: ['youtube.com', 'youtu.be'] },
  // Detection is what gates submissions, so dropping this line is what turns
  // TikTok off in the UI. Playback is direct unless isEmbedOnly('tiktok').
  { platform: 'tiktok', domains: ['tiktok.com'] },
];

export function detectPlatform(url: string): VideoPlatform | null {
  let host: string;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    host = parsed.hostname.toLowerCase();
  } catch {
    return null;
  }
  for (const { platform, domains } of PLATFORM_HOSTS) {
    if (domains.some((d) => host === d || host.endsWith('.' + d))) return platform;
  }
  return null;
}

/**
 * Identity of the video behind a link, ignoring the wrapping: Instagram's
 * `?igsh=` share token, /reels/ vs /reel/, a creator handle in the path, a
 * trailing slash. Two links with the same key play the same clip. Null when
 * the platform isn't recognised.
 */
export function videoKey(url: string): string | null {
  const platform = detectPlatform(url);
  if (!platform) return null;
  if (platform === 'youtube') {
    const id = extractYouTubeId(url);
    return id ? `youtube:${id}` : null;
  }
  if (platform === 'instagram') {
    const m = url.match(/\/(?:reels?|p|tv)\/([A-Za-z0-9_-]+)/);
    return m ? `instagram:${m[1]}` : null;
  }
  const m = url.match(/video\/(\d+)/);
  return m ? `tiktok:${m[1]}` : null;
}

/** Extract a YouTube video ID from various URL formats. */
export function extractYouTubeId(url: string): string | null {
  // youtu.be/ID or youtu.be/ID?t=...
  const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
  if (shortMatch) return shortMatch[1];
  // youtube.com/watch?v=ID
  const watchMatch = url.match(/[?&]v=([^&#]+)/);
  if (watchMatch) return watchMatch[1];
  // youtube.com/shorts/ID or youtube.com/embed/ID
  const pathMatch = url.match(/youtube\.com\/(shorts|embed)\/([^?&#]+)/);
  if (pathMatch) return pathMatch[2];
  return null;
}

/** Build an embed URL for a given video URL. Returns null for unsupported platforms. */
export function getEmbedUrl(url: string): string | null {
  const platform = detectPlatform(url);
  if (!platform) return null;

  switch (platform) {
    case 'instagram':
      // /reels/ (plural, from the web address bar) doesn't serve the embed
      // page — normalize to the canonical /reel/ form first.
      return url.replace(/\/reels\//, '/reel/').replace(/\?.*$/, '').replace(/\/$/, '') + '/embed/';
    case 'youtube': {
      const id = extractYouTubeId(url);
      if (!id) return null;
      // Preserve timestamp if present
      const tMatch = url.match(/[?&]t=([^&#]+)/);
      const start = tMatch ? `?start=${tMatch[1].replace('s', '')}` : '';
      return `https://www.youtube.com/embed/${id}${start}`;
    }
    case 'tiktok': {
      // TikTok video URL: tiktok.com/@user/video/ID
      const idMatch = url.match(/video\/(\d+)/);
      if (!idMatch) return null;
      return `https://www.tiktok.com/embed/v2/${idMatch[1]}`;
    }
  }
}

/**
 * Embed-only mode: play a platform through its own iframe embed instead of the
 * server-extracted direct .mp4, the way YouTube already does. This is the
 * compliance kill switch — turning it on for a platform stops every outbound
 * scrape on that platform's playback path (igvideo, the relay, the video
 * cache), at the cost of the custom player. See functions/SCRAPING_POLICY.md.
 *
 * Per-platform on purpose: a cease-and-desist arrives from one company, not
 * all of them, and pulling Instagram shouldn't take TikTok down with it.
 *
 * Off by default. Three ways to turn it on, most specific first:
 *   - `localStorage.setItem('gnosisEmbedOnly:tiktok', '1')` then reload —
 *     one browser, no rebuild, for trying it. `'0'` forces it back off.
 *   - `localStorage.gnosisEmbedOnly = '1'` — same, but every platform.
 *   - Build with `VITE_EMBED_ONLY_TIKTOK=true` (or `_INSTAGRAM`), or
 *     `VITE_EMBED_ONLY=true` for all of them. This is the one that ships.
 *
 * YouTube ignores all of it — it is always an embed and never extracted.
 */
const EMBED_ONLY_ENV: Record<VideoPlatform, string | undefined> = {
  instagram: import.meta.env.VITE_EMBED_ONLY_INSTAGRAM,
  tiktok: import.meta.env.VITE_EMBED_ONLY_TIKTOK,
  youtube: 'true',
};

export function isEmbedOnly(platform: VideoPlatform): boolean {
  try {
    const scoped = localStorage.getItem(`gnosisEmbedOnly:${platform}`);
    if (scoped !== null) return scoped === '1';
    const all = localStorage.getItem('gnosisEmbedOnly');
    if (all !== null) return all === '1';
  } catch {
    // Private mode / blocked storage — fall through to the build-time value.
  }
  if (EMBED_ONLY_ENV[platform] === 'true') return true;
  return import.meta.env.VITE_EMBED_ONLY === 'true';
}

/** Whether a platform needs server-side video extraction (direct playback). */
export function needsServerExtraction(platform: VideoPlatform): boolean {
  if (platform === 'youtube') return false;
  if (isEmbedOnly(platform)) return false;
  return platform === 'instagram' || platform === 'tiktok';
}

/** Whether a YouTube URL is a Short (portrait 9:16). */
export function isYouTubeShort(url: string): boolean {
  return /youtube\.com\/shorts\//i.test(url);
}

/**
 * Grid-view thumbnail for a technique, or null to draw a fallback tile.
 * Prefers the stored image (the source page's og:image, copied to Firebase
 * Storage on submission); YouTube needs no scrape at all since its thumbnail
 * is derivable from the video id.
 */
export function getThumbnailUrl(sourceUrl?: string, stored?: string): string | null {
  if (stored) return stored;
  if (!sourceUrl) return null;
  const ytId = extractYouTubeId(sourceUrl);
  if (!ytId) return null;
  // hqdefault is a 4:3 frame with the Short's portrait video letterboxed into
  // the middle between blurred bars, which is what cover-cropped onto a tile
  // — so Shorts get oar2, the full-res 9:16 cover, like an Instagram reel's.
  // It isn't minted for every Short; see getThumbnailFallbackUrl.
  const file = isYouTubeShort(sourceUrl) ? 'oar2' : 'hqdefault';
  return `https://img.youtube.com/vi/${ytId}/${file}.jpg`;
}

/**
 * The next thumbnail to try when `src` 404s, or null when there's nothing
 * left: a Short's portrait cover steps down to the always-present hqdefault.
 */
export function getThumbnailFallbackUrl(src: string): string | null {
  const m = src.match(/^https:\/\/img\.youtube\.com\/vi\/([^/]+)\/oar2\.jpg$/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

/** A handle's profile on the site it belongs to. Instagram is the fallback:
 *  it is where most of the catalog comes from, and where a handle with no
 *  evidence either way is likeliest to be. */
export function profileUrlFor(platform: VideoPlatform | null, handle: string): string {
  if (platform === 'youtube') return `https://www.youtube.com/@${handle}`;
  if (platform === 'tiktok') return `https://www.tiktok.com/@${handle}`;
  return `https://instagram.com/${handle}`;
}

/**
 * Where an author handle lives, judged by the clip it was stamped from: a
 * YouTube clip's author is a channel handle, anything else is Instagram.
 */
export function authorProfileUrl(sourceUrl: string | undefined, handle: string): string {
  return profileUrlFor(sourceUrl ? detectPlatform(sourceUrl) : null, handle);
}

/**
 * Which site a creator posts to, judged by the clips of theirs the catalog
 * carries — whichever platform most of them came from. Nobody writes a
 * creator's platform down, and their clips are the only evidence there is, so
 * this is what keeps a creator page from assuming everyone is on Instagram.
 */
export function creatorPlatform(clips: { instagramUrl?: string }[]): VideoPlatform | null {
  const tally = new Map<VideoPlatform, number>();
  for (const c of clips) {
    const p = c.instagramUrl ? detectPlatform(c.instagramUrl) : null;
    if (p) tally.set(p, (tally.get(p) ?? 0) + 1);
  }
  let best: VideoPlatform | null = null;
  for (const [p, n] of tally) if (!best || n > tally.get(best)!) best = p;
  return best;
}
